import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icon';
import { useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import { useDismissableOverlay } from '../../hooks/useDismissableOverlay';

// Below this top-bar width the info group collapses into one button: the search field needs the
// room (#807 R3). Measured on the bar itself, not its contents, so it can't flip back and forth.
const COMPACT_BELOW_PX = 1000;
const PANEL_ESTIMATED_WIDTH_PX = 260;

/** True while `ref`'s element is narrower than the compact threshold. */
export function useCompactWidth(ref: RefObject<HTMLElement | null>): boolean {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setCompact(element.getBoundingClientRect().width < COMPACT_BELOW_PX);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return compact;
}

interface TopBarInfoProps {
  /** The formula (or Markush chip), the match count and Graph stats. */
  readonly children: ReactNode;
  /** The number on the collapsed button's badge (the match count); null hides the badge. */
  readonly count: number | null;
  /** #1110 P7: open structure problems -- on a narrow bar a dot on the button is all that tells. */
  readonly problems?: number;
  readonly compact: boolean;
}

/**
 * #807 R3 (owner, from the mockup review) -- the drawing's formula, its match count and Graph
 * stats sit in the top bar, between the search field and the account icon, instead of at the end
 * of the paper toolbar. Shown whole while the bar has room; on a narrow bar, one info button with
 * the match count as a badge opens them in a panel.
 */
export function TopBarInfo({ children, count, problems = 0, compact }: TopBarInfoProps) {
  const { t } = useTranslation();
  const [requestedOpen, setOpen] = useState(false);
  // Widening the bar past the threshold shows the group inline; the panel is then simply closed.
  const open = requestedOpen && compact;
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const anchor = useAnchoredPopover(open, triggerRef, panelRef, () => PANEL_ESTIMATED_WIDTH_PX);
  useDismissableOverlay(open, setOpen, triggerRef, panelRef);
  if (!compact) {
    return (
      <div
        className="workspace-topbar-info"
        role="group"
        aria-label={t('workspace.info.label')}
        data-testid="topbar-info"
      >
        {children}
      </div>
    );
  }

  const countLabel = count === null ? t('workspace.info.label') : t('workspace.info.labelWithCount', { count });
  const label = problems > 0 ? `${countLabel}, ${t('drawingInfo.problems', { count: problems })}` : countLabel;
  return (
    <div className="workspace-topbar-info-disclosure">
      <button
        ref={triggerRef}
        type="button"
        className="icon-button workspace-topbar-info-toggle"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        title={label}
        onClick={() => setOpen((value) => !value)}
        data-testid="topbar-info-toggle"
      >
        <Icon name="monitoring" />
        {count === null ? null : (
          <span className="workspace-topbar-info-badge" aria-hidden="true">
            {count > 999 ? '999+' : count}
          </span>
        )}
        {problems > 0 ? (
          <span className="workspace-topbar-info-alert" aria-hidden="true" data-testid="topbar-info-alert" />
        ) : null}
      </button>
      {/* Kept mounted while collapsed (hidden when closed) so Graph stats keeps its own state. */}
      <div
        ref={panelRef}
        id={panelId}
        role="dialog"
        aria-label={t('workspace.info.label')}
        className="workspace-topbar-info-panel"
        data-testid="topbar-info"
        hidden={!open}
        // First open frame: laid out but invisible, so useAnchoredPopover measures its real width
        // (a display: none panel measures 0 and would be placed off the right edge).
        style={anchor ? { top: anchor.top, left: anchor.left } : { visibility: 'hidden' }}
      >
        {children}
      </div>
    </div>
  );
}
